import React from "react";
import { Badge } from "../Badge";
import { Button } from "../Button";
import { getReasonLabel } from "../TipTapEditor/utils/suggestionLabels";
import { Body, Heading } from "../Typography";
import "./SuggestionPopoverContent.css";
import { Icon } from "../Icons";
import { parseSegmentLinks } from "./utils/parseSegmentLinks";

export type SuggestionPopoverVariant = "default" | "minimal";

export interface SuggestionPopoverContentProps {
  /**
   * Layout variant:
   * - "default": Vertical layout with full details (reason, explanation, confidence)
   * - "minimal": Horizontal compact layout with just reason and actions
   */
  variant?: SuggestionPopoverVariant;

  /**
   * Badge label displayed at the top (e.g., "Suggested Removal", "Suggested Delete")
   */
  badgeLabel: string;

  /**
   * Reason code for the suggestion (will be converted to human-readable label)
   */
  reason: string;

  /**
   * Detailed explanation for the suggestion
   */
  reasonDetails?: string | null;

  /** Retained source entry for an inline previously-mentioned reference. */
  relatedSegmentID?: string | null;

  /**
   * AI confidence score (0-1)
   */
  confidence?: number | null;

  /**
   * Whether the suggestion is still valid (content hasn't changed).
   * When false, the Accept button is disabled.
   * Defaults to true.
   */
  isValid?: boolean;

  /**
   * Callback when suggestion is accepted
   */
  onAccept: () => void;

  /**
   * Callback when suggestion is rejected/ignored
   */
  onReject: () => void;

  /**
   * Callback when "View source entry" link is clicked.
   * When provided, renders a navigation link in the popover.
   */
  onViewRelatedSegment?: () => void;

  /**
   * Callback to navigate to a segment by its logical segment ID.
   * When provided, UUIDs in reasonDetails are rendered as clickable links.
   */
  onNavigateToSegment?: (segmentId: string) => void;
}

/**
 * Renders text with UUID segments as clickable links.
 * Falls back to plain text when onNavigateToSegment is not provided.
 */
const renderTextWithSegmentLinks = (
  text: string,
  onNavigateToSegment?: (segmentId: string) => void,
): React.ReactNode => {
  if (!onNavigateToSegment) return text;

  const parts = parseSegmentLinks(text);
  if (parts.length === 1 && parts[0].type === "text") return text;

  return parts.map((part, i) =>
    part.type === "uuid" ? (
      <Button
        key={i}
        className="p-0"
        variant="link"
        size="sm"
        aria-label="Navigate to related segment"
        onClick={(e) => {
          e.stopPropagation();
          onNavigateToSegment(part.value);
        }}
      >
        {part.value.slice(0, 8)}
      </Button>
    ) : (
      part.value
    ),
  );
};

/**
 * Shared content component for suggestion popovers.
 *
 * Used by both SuggestionBubbleMenu (TipTap editor) and SuggestionPopover.
 * Provides consistent UI for displaying AI suggestions with Accept/Reject actions.
 */
export const SuggestionPopoverContent: React.FC<
  SuggestionPopoverContentProps
> = ({
  variant = "default",
  badgeLabel,
  reason,
  reasonDetails,
  relatedSegmentID,
  confidence,
  isValid = true,
  onAccept,
  onReject,
  onViewRelatedSegment,
  onNavigateToSegment,
}) => {
  if (variant === "minimal") {
    return (
      <div className="indigo-suggestion-popover-content--minimal">
        <Badge label={badgeLabel} size="lg" />
        <div className="indigo-suggestion-popover-content__reason">
          <Body size="md">{getReasonLabel(reason)}</Body>
        </div>
        <div className="indigo-suggestion-popover-content__actions">
          <Button
            variant="ghost"
            size="md"
            onClick={onReject}
            icon={<Icon name="close" sizeX={14} sizeY={14} />}
            title="Ignore suggestion"
            aria-label="Ignore suggestion"
          >
            Ignore
          </Button>
          <Button
            variant="ghost"
            size="md"
            onClick={onAccept}
            disabled={!isValid}
            icon={<Icon name="check" sizeX={14} sizeY={14} />}
            title={
              isValid
                ? "Accept suggestion (delete text)"
                : "Cannot accept - content changed"
            }
            aria-label="Accept suggestion"
          >
            Accept
          </Button>
        </div>
      </div>
    );
  }

  // Default variant - detailed vertical layout
  return (
    <div className="indigo-suggestion-popover-content--default">
      <div className="indigo-suggestion-popover-content__header">
        <Badge label={badgeLabel} size="sm" />
        <Heading level={3}>{getReasonLabel(reason)}</Heading>
      </div>

      {reasonDetails && (
        <Body size="md" className="font-normal">
          {renderTextWithSegmentLinks(reasonDetails, onNavigateToSegment)}
          {reason === "DUPLICATE_IN_ANOTHER_SEGMENT" && relatedSegmentID && (
            <>
              {" Source entry: "}
              {onViewRelatedSegment ? (
                <Button
                  className="p-0"
                  variant="link"
                  size="sm"
                  aria-label="Navigate to source entry"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewRelatedSegment();
                  }}
                >
                  {relatedSegmentID.slice(0, 8)}
                </Button>
              ) : (
                relatedSegmentID.slice(0, 8)
              )}
              {"."}
            </>
          )}
        </Body>
      )}

      {onViewRelatedSegment && (
        <Button
          variant="link"
          size="sm"
          icon={<Icon name="link" />}
          onClick={(e) => {
            e.stopPropagation();
            onViewRelatedSegment();
          }}
        >
          View source entry
        </Button>
      )}

      <div className="indigo-suggestion-popover-content__footer">
        {confidence != null && (
          <Body size="sm" className="text-indigo-text-disabled">
            Confidence: {confidence}
          </Body>
        )}
        <div className="flex-1"></div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onReject}
          icon={<Icon name="close" sizeX={14} sizeY={14} />}
          title="Ignore suggestion"
          aria-label="Ignore suggestion"
        >
          Ignore
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onAccept}
          disabled={!isValid}
          icon={<Icon name="check" sizeX={14} sizeY={14} />}
          title={
            isValid
              ? "Accept suggestion (delete text)"
              : "Cannot accept - content changed"
          }
          aria-label="Accept suggestion"
        >
          Accept
        </Button>
      </div>
    </div>
  );
};
